import { Box, Collapse, Divider, Paper, Skeleton } from "@mui/material";
import React from "react";

const CREWS = [3, 1]; // first crew: 3 workers, second crew: 2 workers
const DAYS = 7;
const BORDER_COLOR = "#E5E5E5";

function DateHeaderSkeleton() {
	return (
    <Box display="flex" width="100%" minWidth={1376} sx={{ flexShrink: 0 }}>
      {/* Left static column */}
      <Box
        sx={{
          flexBasis: "12.5%",
          minWidth: "164px",
          flexShrink: 0,
        }}
      >
        <Skeleton variant="rectangular" width="100%" height={64} />
      </Box>

      {/* Right scrollable container */}
      <Box
        display="flex"
        overflow="auto"
        sx={{
          flex: 1,
          minWidth: 1204, // Optional guard
        }}
      >
        {Array.from({ length: DAYS }).map((_, i) => (
          <React.Fragment key={i}>
            {i > 0 && (
              <Box
                sx={{
                  width: "1px",
                  backgroundColor: BORDER_COLOR,
                  flexShrink: 0,
                }}
              />
            )}
            <Box
              sx={{
                flexBasis: "14.28%",
                minWidth: "164px",
                paddingLeft: 1,
                height: 64,
                borderTop: `1px solid ${BORDER_COLOR}`,
                borderBottom: `1px solid ${BORDER_COLOR}`,
                flexShrink: 0,
              }}
            >
              <Skeleton variant="text" width={80} height={28} />
              <Skeleton variant="rounded" width={40} height={32} />
            </Box>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

function CrewHeaderSkeleton() {
	return (
		<>
			{/* Horizontal divider above crew header */}
			<Divider sx={{ borderColor: BORDER_COLOR }} />
			<Box
				sx={{
					display: "flex",
					flexDirection: "row",
					alignItems: "center",
					gap: "8px",
					pl: 2,
					py: 2,
					borderTop: `1px solid ${BORDER_COLOR}`,
					borderBottom: `1px solid ${BORDER_COLOR}`,
				}}
			>
				<Skeleton variant='circular' width={24} height={24} />
				<Skeleton
					variant='text'
					width={94}
					height={32}
					sx={{ marginLeft: "4px" }}
				/>
			</Box>
		</>
	);
}

function WorkerDetailsSkeleton() {
	return (
		<Box
			sx={{
				width: "12.5%",
				pl: 2,
				pt: 1,
				display: "flex",
				flexDirection: "column",
				alignItems: "flex-start",
				borderTop: `1px solid ${BORDER_COLOR}`,
			}}
		>
			<Skeleton variant='circular' width={24} height={24} sx={{ mb: 1 }} />
			<Skeleton variant='text' width={100} height={20} />
			<Skeleton variant='text' width={80} height={16} />
			<Skeleton variant='text' width={60} height={14} />
		</Box>
	);
}

function ShiftCardSkeleton() {
	return (
		<Paper
			sx={{
				m: 1,
				minHeight: "90px",
				borderRadius: 2,
				p: 1,
				border: "1px solid #CAC4D0",
				borderLeft: "6px solid #e0e0e0",
				backgroundColor: "#f0f0f0",
			}}
		>
			<Skeleton variant='text' width='60%' height={22} />
			<Skeleton variant='text' width='40%' height={20} />
		</Paper>
	);
}

function WorkerRowSkeleton({ crewIdx }: { crewIdx: number }) {
	const crewSkipDays = [
		[1, 4],
		[2, 5],
	];
	const skipDays = crewSkipDays[crewIdx] || [];
	return (
		<>
			{/* Horizontal divider above each worker row */}
			<Divider sx={{ borderColor: BORDER_COLOR }} />
			<Box display='flex' width='100%'>
				<WorkerDetailsSkeleton />
				<Divider orientation='vertical' flexItem />
				<Box display='flex' flex={1}>
					{Array.from({ length: DAYS }).map((_, i) => (
						<React.Fragment key={i}>
							{/* Add vertical divider before each day except the first */}
							{i > 0 && (
								<Box
									sx={{
										width: "1px",
										backgroundColor: BORDER_COLOR,
										flexShrink: 0,
									}}
								/>
							)}
							<Box
								flex={1}
								minWidth={120}
								borderTop={`1px solid ${BORDER_COLOR}`}
							>
								{skipDays.includes(i) ? (
									<Box sx={{ height: "72px" }} />
								) : (
									<ShiftCardSkeleton />
								)}
							</Box>
						</React.Fragment>
					))}
				</Box>
			</Box>
		</>
	);
}

export default function WorkerSchedulerSkeleton({
	isWorkerFiltersStatus,
	isWorkerShiftsStatus,
}: {
	isWorkerFiltersStatus: boolean;
	isWorkerShiftsStatus: boolean;
}) {

	return (
		<Box width='100%'>
			{(isWorkerShiftsStatus || isWorkerFiltersStatus) && (
				<>
				<DateHeaderSkeleton />
					{CREWS.map((workers, crewIdx) => (
						<Box key={crewIdx} mb={2}>
							<CrewHeaderSkeleton />
							<Collapse in>
								{Array.from({ length: workers }).map((_, workerIdx) => (
									<WorkerRowSkeleton key={workerIdx} crewIdx={crewIdx} />
								))}
							</Collapse>
						</Box>
					))}
				</>
			)}
		</Box>
	);
}
